import { createContext, useCallback, useContext, useState, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';

import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';

import useFlowState from '#client/hooks/flow/useFlowState';

import { SECTION_KEYS } from '../constants/sections';

const CheckoutContext = createContext(null);

// Initialize each section's state to false
const initialSectionState = Object.values(SECTION_KEYS).reduce(
  (acc, section) => ({ ...acc, [section]: false }),
  {}
);

export function CheckoutProvider({ children }) {
  const [validSections, setValidSections] = useState(initialSectionState);
  const [editingSections, setEditingSections] = useState(initialSectionState);
  const { get } = useFlowState();

  // TODO: Get businessInfo and contactInfo values to use for default values, too.
  const formationInfo = useMemo(() => get('formationInfo') || {}, [get]);

  // Initialize form with default values from flow state
  const defaultValues = useMemo(
    () => ({
      [SECTION_KEYS.BUSINESS_INFO]: {
        name: formationInfo.businessName || '',
        indicator: formationInfo.businessInfo?.indicator || 'LLC'
      },
      [SECTION_KEYS.PRINCIPAL_ADDRESS]: {
        address1: formationInfo.principalAddress?.address1 || '',
        address2: formationInfo.principalAddress?.address2 || '',
        city: formationInfo.principalAddress?.city || '',
        state: formationInfo.principalAddress?.state || '',
        postalCode: formationInfo.principalAddress?.postalCode || ''
      },
      [SECTION_KEYS.MAILING_ADDRESS]: {
        address1: formationInfo.mailingAddress?.address1 || '',
        address2: formationInfo.mailingAddress?.address2 || '',
        city: formationInfo.mailingAddress?.city || '',
        state: formationInfo.mailingAddress?.state || '',
        postalCode: formationInfo.mailingAddress?.postalCode || ''
      },
      [SECTION_KEYS.CONTACT_INFO]: {
        firstName: formationInfo.owner?.firstName || '',
        lastName: formationInfo.owner?.lastName || '',
        email: formationInfo.owner?.email || '',
        phone: formationInfo.owner?.phone || ''
      },
      [SECTION_KEYS.ACCOUNT_INFO]: {
        email: formationInfo.owner?.email || ''
      }
    }),
    [formationInfo]
  );

  const formMethods = useForm({
    defaultValues,
    mode: 'onBlur' // Validate when fields lose focus
  });

  // Start editing a section
  const editSection = useCallback((section) => {
    setEditingSections((prev) => ({ ...prev, [section]: true }));
  }, []);

  // Cancel editing without saving
  const cancelEdit = useCallback((section) => {
    setEditingSections((prev) => ({ ...prev, [section]: false }));
  }, []);

  // Mark a section as valid
  const markSectionValid = useCallback((section) => {
    setValidSections((prev) => ({ ...prev, [section]: true }));
  }, []);

  // Invalidate a section
  const invalidateSection = useCallback((section) => {
    setValidSections((prev) => ({ ...prev, [section]: false }));
  }, []);

  // Validate section and update validity state
  // Options:
  //   - showErrors: whether to show validation errors (default: true)
  //   - shouldFocus: whether to focus on the first error field (default: true)
  const validateSection = useCallback(
    async (section, options) => {
      const { shouldFocus = true, showErrors = true } = options || {};

      // Run validation
      const isValid = await formMethods.trigger(section, { shouldFocus });

      // Update section validity
      if (isValid) {
        markSectionValid(section);
      } else {
        invalidateSection(section);

        // Clear errors if we don't want to show them
        if (!showErrors) {
          formMethods.clearErrors(section);
        }
      }

      return isValid;
    },
    [formMethods, invalidateSection, markSectionValid]
  );

  // Complete a section (validate, mark as valid if passes, and exit edit mode)
  const completeSection = useCallback(
    async (section) => {
      // const isValid = await validateSection(section, { showErrors: true });
      // if (isValid) { cancelEdit(section) }
      // return isValid;
      cancelEdit(section);
      markSectionValid(section);
    },
    [cancelEdit, markSectionValid]
  );

  // Silently validate all fields on initial load
  useEffect(() => {
    const validateAllSections = async () => {
      const validationOptions = {
        shouldFocus: false,
        showErrors: false // Validate silently (without showing errors)
      };
      try {
        await validateSection(SECTION_KEYS.BUSINESS_INFO, validationOptions);
        await validateSection(SECTION_KEYS.PRINCIPAL_ADDRESS, validationOptions);
        await validateSection(SECTION_KEYS.MAILING_ADDRESS, validationOptions);
        await validateSection(SECTION_KEYS.CONTACT_INFO, validationOptions);
      } catch (error) {
        console.error('Error during silent validation:', error);
      }
    };

    validateAllSections();
  }, [validateSection]);

  const value = useMemoObject({
    cancelEdit,
    completeSection,
    editSection,
    editingSections,
    formMethods,
    invalidateSection,
    markSectionValid,
    validateSection,
    validSections
  });

  return (
    <CheckoutContext.Provider value={value}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </CheckoutContext.Provider>
  );
}

CheckoutProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}
